import React, { useState, useEffect } from 'react';
import { Clock, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { 
  getDisplayStatus,
  getStatusMessage,
  getStatusEmoji,
  getStatusColor,
  getStatusBgColor
} from '@/utils/displayStatusUtils';
import { detectTimezone } from '@/utils/timezoneUtils';
import { DisplaySchedule, DisplayStatus } from '@/types/display';
import { cn } from '@/lib/utils';

interface DisplayStatusBadgeProps {
  schedule: DisplaySchedule;
  city?: string | null;
  state?: string | null;
  timezone?: string;
  className?: string;
  showRefresh?: boolean;
  refreshInterval?: number; // in seconds, default 60
  variant?: 'default' | 'compact' | 'detailed';
}

const DisplayStatusBadge: React.FC<DisplayStatusBadgeProps> = ({
  schedule,
  city,
  state,
  timezone,
  className,
  showRefresh = false,
  refreshInterval = 60,
  variant = 'default'
}) => {
  const [status, setStatus] = useState<DisplayStatus | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Calculate status
  const calculateStatus = React.useCallback(() => {
    try {
      const fallbackTimezone = timezone || detectTimezone(city, state);
      const newStatus = getDisplayStatus(schedule, fallbackTimezone);
      setStatus(newStatus);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error in DisplayStatusBadge calculateStatus:', error);
      // Set error state
      const fallbackTimezone = timezone || detectTimezone(city, state) || 'America/New_York';
      setStatus({
        status: 'no-schedule',
        message: 'Error loading schedule status',
        currentTime: new Date().toISOString(),
        timezone: fallbackTimezone,
        reason: 'component-error'
      });
      setLastUpdated(new Date());
    }
  }, [schedule, city, state, timezone]);

  // Initial calculation and interval setup
  useEffect(() => {
    calculateStatus();
    
    if (refreshInterval > 0) {
      const interval = setInterval(calculateStatus, refreshInterval * 1000);
      return () => clearInterval(interval);
    }
  }, [calculateStatus, refreshInterval]);

  // Manual refresh handler
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX
      calculateStatus();
    } catch (error) {
      console.error('Error refreshing status:', error);
      // Set error state but don't throw to prevent UI break
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!status) {
    return (
      <Badge variant="outline" className={cn("animate-pulse", className)}>
        <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
        Loading status...
      </Badge>
    );
  }

  const emoji = getStatusEmoji(status.status);
  const message = getStatusMessage(status);
  const colorClass = getStatusColor(status.status);
  const bgColorClass = getStatusBgColor(status.status);

  // Format time until change
  const formatTimeUntilChange = () => {
    if (!status.minutesUntilChange || !status.nextChange) return null;
    
    const minutes = status.minutesUntilChange;
    const action = status.nextChange === 'closing' ? 'Closing' : 'Opening';
    
    if (minutes < 60) {
      return `${action} in ${minutes}m`;
    } else if (minutes < 1440) {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      if (remainingMins === 0) {
        return `${action} in ${hours}h`;
      }
      return `${action} in ${hours}h ${remainingMins}m`;
    } else {
      const days = Math.floor(minutes / 1440);
      return `${action} in ${days}d`;
    }
  };

  const timeUntilChange = formatTimeUntilChange();

  // Compact variant
  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge 
              variant="outline" 
              className={cn(
                "cursor-help transition-colors",
                bgColorClass,
                className
              )}
            >
              <span className="text-sm">{emoji}</span>
              <span className={cn("ml-1 font-medium", colorClass)}>
                {status.status === 'on' ? 'On' : 
                 status.status === 'off' ? 'Off' : 
                 status.status === 'no-schedule' ? 'No Schedule' : 'Off Season'}
              </span>
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            <div className="text-sm">
              <div className="font-medium">{message}</div>
              {timeUntilChange && (
                <div className="text-muted-foreground">{timeUntilChange}</div>
              )}
              <div className="text-xs text-muted-foreground mt-1">
                Updated {lastUpdated.toLocaleTimeString()}
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  // Detailed variant
  if (variant === 'detailed') {
    return (
      <div className={cn(
        "border rounded-lg p-3 space-y-2 transition-colors",
        bgColorClass,
        className
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <span className="text-lg">{emoji}</span>
            <span className={cn("font-semibold", colorClass)}>
              {message}
            </span>
          </div>
          {showRefresh && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="h-6 w-6 p-0"
            >
              <RefreshCw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
            </Button>
          )}
        </div>
        
        {timeUntilChange && (
          <div className="flex items-center space-x-1 text-sm text-muted-foreground">
            <Clock className="h-3 w-3" />
            <span>{timeUntilChange}</span>
          </div>
        )}
        
        {status.todayHours && (
          <div className="text-xs text-muted-foreground">
            Today: {status.todayHours.start} - {status.todayHours.end}
          </div>
        )}
        
        {status.status === 'off-season' && status.seasonStart && status.seasonEnd && (
          <div className="text-xs text-muted-foreground">
            Season: {new Date(status.seasonStart).toLocaleDateString()} - {new Date(status.seasonEnd).toLocaleDateString()}
          </div>
        )}
        
        <div className="text-xs text-muted-foreground">
          Updated {lastUpdated.toLocaleTimeString()}
          {status.timezone && (
            <span className="ml-1">({status.timezone.split('/')[1]?.replace('_', ' ')})</span>
          )}
        </div>
      </div>
    );
  }

  // Default variant
  return (
    <Badge 
      variant="outline" 
      className={cn(
        "h-8 px-3 transition-colors",
        bgColorClass,
        className
      )}
    >
      <span className="text-sm mr-2">{emoji}</span>
      <div className="flex flex-col items-start">
        <span className={cn("font-semibold text-sm leading-none", colorClass)}>
          {status.status === 'on' ? 'Lights Are On' : 
           status.status === 'off' ? 'Lights Are Off' : 
           status.status === 'no-schedule' ? 'No Schedule Posted' : 'Off Season'}
        </span>
        {timeUntilChange && (
          <span className="text-xs text-muted-foreground leading-none mt-0.5">
            {timeUntilChange}
          </span>
        )}
      </div>
      {showRefresh && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isRefreshing}
          className="h-6 w-6 p-0 ml-2"
        >
          <RefreshCw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
        </Button>
      )}
    </Badge>
  );
};

export default DisplayStatusBadge;